import React from "react";
import { useTranslation } from "react-i18next";
import Toggle from "components/form/toggle/Toggle";
import { Select } from "components/select/index";
import SelectNew from "components/select/SelectNew";
import { TAccountingReference, TTaxLines } from "redux/reducers/models/facility";
import DefaultsTableLayout from "./DefaultsTableLayout";
import "./financialTable.scss";
import {
  accountingReferenceMatchesQuery,
  formatAccountingReferenceLabel,
} from "utils/formatAccountingReferenceLabel";

interface IFinancialState {
  taxLinesEnabled?: boolean;
  taxExemptEnabled?: boolean;
  cannotBeSoldEnabled?: boolean;
  accounting_reference_id?: number;
  tax_line_ids?: number[];
  tax_exempt?: boolean;
  cannot_be_sold?: boolean;
}


interface IFinancialSearch {
  accountRefSearch: string;
}

interface IFinancialTableProps {
  state: IFinancialState;
  setState: React.Dispatch<React.SetStateAction<IFinancialState>>;

  search: IFinancialSearch;
  onSearch: (key: string, query: string) => void;
  accountingReferences: TAccountingReference[];
  taxLines: TTaxLines[];
  taxNamesPlaceholder: string;
  onTaxLinesToggle: (value: number) => void;
  onTaxLinesClear?: () => void;
  /** When true, all toggles in this table are disabled. */
  togglesDisabled?: boolean;
}

export default function FinancialTable(props: IFinancialTableProps) {
  const { t } = useTranslation();
  const {
    state,
    setState,
    search,
    onSearch,
    accountingReferences,
    taxLines,
    taxNamesPlaceholder,
    onTaxLinesToggle,
    onTaxLinesClear,
    togglesDisabled,
  } = props;

  return (
    <DefaultsTableLayout tooltipId="financial-default-value-tooltip">
      {/* Accounting Reference */}
      <tr className="financial-defaults-table-row">
        <td>{t("Accounting reference")}</td>
     
        <td>
          <Select
            placeholder={t("Search Accounting Reference...")}
            className="flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200"
            onSearch={(query: string) => onSearch("accountRefSearch", query)}
            onChange={(value: number) =>
              setState((prev: IFinancialState) => ({ ...prev, accounting_reference_id: value }))
            }
            searchValue={search.accountRefSearch || ""}
            showSearch={true} // filters.accountingReferenceEnabled
            showDropDownOnFocus={false}
            hideDropdown
            disabled={false} // !filters.accountingReferenceEnabled
            allowClear
            // defaultValue={state.accounting_reference_id}
          >
            {accountingReferences
              ?.filter(ref =>
                accountingReferenceMatchesQuery(
                  ref.title ?? "",
                  ref.reference_number,
                  search.accountRefSearch ?? "",
                ),
              )
              ?.map(reference => {
                const label = formatAccountingReferenceLabel(
                  reference.title ?? "",
                  reference.reference_number,
                );
                return (
                  <Select.Option key={reference.id} value={reference.id} name={label}>
                    {label}
                  </Select.Option>
                );
              })}
          </Select>
        </td>
      </tr>

      {/* Tax Lines */}
      <tr>
        <td>{t("Tax lines")}</td>
      
        <td>
          <SelectNew
            id="tax-lines-select"
            placeholder={taxNamesPlaceholder || t("Select tax lines")}
            multiValue={state.tax_line_ids || []}
            options={(taxLines ?? []).map(taxLine => ({
              id: taxLine.id,
              label: taxLine.name,
            }))}
            onChange={(value: number) => onTaxLinesToggle(value)}
            onClear={onTaxLinesClear}
            disabled={false} // !state.taxLinesEnabled
          />
        </td>
      </tr>

      {/* Tax Exempt */}
      <tr>
        <td>{t("Tax exempt")}</td>
      
        <td>
          <Toggle
            id="tax_exempt"
            checked={state.tax_exempt}
            onChange={() => setState((prev: IFinancialState) => ({ ...prev, tax_exempt: !prev.tax_exempt }))}
            disabled={togglesDisabled ?? false}
          />
        </td>
      </tr>

      {/* Cannot Be Sold */}
      <tr>
        <td>{t("Cannot be sold")}</td>
  
        <td>
          <Toggle
            id="cannot_be_sold"
            checked={state.cannot_be_sold}
            onChange={() => setState((prev: IFinancialState) => ({ ...prev, cannot_be_sold: !prev.cannot_be_sold }))}
            disabled={togglesDisabled ?? false}
          />
        </td>
      </tr>
    </DefaultsTableLayout>
  );
}
